import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { AccountWithConvertedBalance } from "@/types/account-drilldown";
import CustomTooltip from "./CustomToolTip";
import { formatAccountType } from "@/lib/account-utils";
import { formatCurrency } from "@/lib/currency.utils";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";

interface AccountDistributionChartProps {
  accounts: AccountWithConvertedBalance[];
  baseCurrency: string;
  isLoading: boolean;
  selectedCurrency?: string;
  accountColors?: Map<string, string>;
}

/**
 * Account Distribution Chart Component
 * Displays pie chart showing percentage distribution of balances across accounts
 * Excludes accounts with zero or negative balances
 */
export function AccountDistributionChart({
  accounts,
  baseCurrency,
  isLoading,
  selectedCurrency,
  accountColors,
}: AccountDistributionChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Account Distribution</CardTitle>
          <CardDescription>Loading account distribution...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <div className="h-48 w-48 rounded-full bg-muted animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  // Filter out accounts with zero or negative balances
  const validAccounts = accounts.filter(
    (account) => account.convertedBalance > 0,
  );

  if (validAccounts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Account Distribution</CardTitle>
          <CardDescription>No accounts with positive balances</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No data to display</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate total for percentage calculation
  const total = validAccounts.reduce(
    (sum, account) => sum + account.convertedBalance,
    0,
  );

  // Prepare data for pie chart — use Display_Name and disambiguate duplicates
  const rawChartData = validAccounts.map((account) => ({
    id: account.id,
    displayName: getDisplayName(account),
    account_number: account.account_number,
    value: account.convertedBalance,
    type: account.type,
    percentage: (account.convertedBalance / total) * 100,
    color: accountColors.get(account.id) || "hsl(var(--muted))",
    bankName: account.bank_name,
    accountNumber: account.account_number,
  }));

  const disambiguated = disambiguateDisplayNames(rawChartData);

  const chartData = disambiguated.map((item) => ({
    ...item,
    name: item.displayName,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account Distribution</CardTitle>
        <CardDescription>
          Balance distribution across accounts in {selectedCurrency}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          role="img"
          aria-label={`Account distribution pie chart showing ${chartData.length} accounts in ${selectedCurrency}`}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart tabIndex={0} aria-label="Account distribution pie chart">
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percentage }) => `${percentage.toFixed(1)}%`}
                outerRadius={80}
                fill="hsl(var(--primary))"
                dataKey="value">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${entry.id}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip selectedCurrency={selectedCurrency} />}
              />
              <Legend
                formatter={(value, entry: any) => entry.payload.name}
                wrapperStyle={{ fontSize: "12px" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Screen reader accessible data table */}
        <div className="sr-only">
          <table>
            <caption>Account distribution data</caption>
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Account Type</th>
                <th>Balance</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((entry, index) => (
                <tr key={index}>
                  <td>{entry.name}</td>
                  <td>{
                    formatAccountType(entry.type)
                  }</td>
                  <td>{formatCurrency(entry.value, selectedCurrency)}</td>
                  <td>{entry.percentage.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
